'use client'

import React, { useState } from 'react'
import { ClipboardCopy } from 'lucide-react'
import { watchClasses } from './processedTypes'

export function ExpandToggle({ count, open, onToggle, label, c }: {
  count: number; open: boolean; onToggle: () => void; label: string
  c: ReturnType<typeof watchClasses>
}) {
  return (
    <button
      onClick={onToggle}
      className={`font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal ${c.faint}`}
    >
      {open ? `↑ Hide ${count} ${label}` : `↓ Show ${count} ${label}`}
    </button>
  )
}

export function CopyChip({ value, display }: { value: string; display?: string }) {
  const [copied, setCopied] = useState(false)
  const label = display ?? (value.length > 12 ? value.slice(0, 8) + '…' : value)
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    const markCopied = () => { setCopied(true); setTimeout(() => setCopied(false), 1500) }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(markCopied).catch(() => {
        const el = document.createElement('textarea')
        el.value = value
        el.style.position = 'fixed'
        el.style.opacity = '0'
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        markCopied()
      })
    } else {
      const el = document.createElement('textarea')
      el.value = value
      el.style.position = 'fixed'
      el.style.opacity = '0'
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      markCopied()
    }
  }
  return (
    <span
      className="group inline-flex items-center gap-1 cursor-pointer"
      onClick={handleCopy}
      title={value}
    >
      <span className={`font-mono text-[11px] transition-colors ${copied ? 'text-success' : 'text-muted-foreground group-hover:text-signal'}`}>
        {copied ? '✓ copied' : label}
      </span>
      <ClipboardCopy className={`h-3 w-3 shrink-0 transition-opacity ${copied ? 'opacity-0' : 'opacity-0 group-hover:opacity-60'} text-signal`} />
    </span>
  )
}

export function Highlight({ text, query }: { text: string | null | undefined; query: string }) {
  const t = text ?? ''
  if (!query || !t) return <>{t}</>
  const parts: React.ReactNode[] = []
  const lower = t.toLowerCase()
  const q = query.toLowerCase()
  let cursor = 0
  let idx: number
  while ((idx = lower.indexOf(q, cursor)) !== -1) {
    if (idx > cursor) parts.push(t.slice(cursor, idx))
    parts.push(<mark key={idx} className="bg-warning-surface text-warning rounded-[2px] px-[1px]">{t.slice(idx, idx + q.length)}</mark>)
    cursor = idx + q.length
  }
  if (cursor < t.length) parts.push(t.slice(cursor))
  return <>{parts}</>
}

export function CampField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">{label}</span>
      {children}
    </label>
  )
}
